import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { MailCheck, Smartphone, ArrowRight, Loader2 } from "lucide-react"; // Ho cambiato l'icona in Smartphone per contesto mobile
import { LOGO_URL } from "@/constants";
import { useTranslation } from "react-i18next";

const MobileAuthConfirmed: React.FC = () => {
  const { t } = useTranslation();
  const [count, setCount] = useState(5);

  // 1. Costruiamo il deep link a livello di componente (fuori dall'useEffect)
  // in modo che sia accessibile sia al timer che al click.
  const getDeepLink = useCallback(() => {
    const hash = window.location.hash;
    return `clip://auth/callback${hash}`;
  }, []);

  // 2. Funzione unica per gestire il reindirizzamento
  const handleDeepLinkNavigation = useCallback(() => {
    const deepLink = getDeepLink();
    window.location.href = deepLink;
  }, [getDeepLink]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCount((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          // 3. Il timer attiva la stessa funzione del click
          handleDeepLinkNavigation();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [handleDeepLinkNavigation]);

  return (
    <div className="min-h-screen bg-white font-sans selection:bg-clip-100 relative overflow-hidden flex items-center justify-center">
      {/* Animated Background Blobs */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <motion.div
          animate={{
            x: [0, 100, 0],
            y: [0, -50, 0],
            scale: [1, 1.2, 1],
          }}
          transition={{
            opacity: { duration: 1.5, ease: "easeOut" },
            default: { duration: 20, repeat: Infinity, ease: "easeInOut" }
          }}
          className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] bg-green-100/40 rounded-full blur-[100px]"
        />
        <motion.div
          animate={{
            x: [0, -100, 0],
            y: [0, 100, 0],
            scale: [1, 1.3, 1],
          }}
          transition={{
            opacity: { duration: 1.5, ease: "easeOut" },
            default: { duration: 25, repeat: Infinity, ease: "easeInOut" }
          }}
          className="absolute bottom-[-10%] right-[-10%] w-[600px] h-[600px] bg-clip-100/30 rounded-full blur-[120px]"
        />
      </div>

      {/* Navbar Minimal */}
      <nav className="absolute top-0 left-0 right-0 p-6 flex justify-center z-50">
        <img src={LOGO_URL} alt="Clip Logo" className="h-8 w-auto object-contain" />
      </nav>

      <div className="relative z-10 w-full max-w-lg px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white/80 backdrop-blur-xl border border-gray-100 shadow-2xl shadow-gray-200/50 rounded-3xl p-8 md:p-12 text-center"
        >
          {/* Success Icon Animation */}
          <div className="mb-8 flex justify-center">
            <div className="relative">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{
                  type: "spring",
                  stiffness: 260,
                  damping: 20,
                  delay: 0.1,
                }}
                className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center shadow-lg shadow-green-500/30 relative z-10"
              >
                <MailCheck className="text-white w-12 h-12" strokeWidth={2.5} />
              </motion.div>
              <motion.div
                animate={{
                  scale: [1, 1.5],
                  opacity: [0, 0.3, 0]
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeOut"
                }}
                className="absolute inset-0 bg-green-400 rounded-full z-0"
              />
            </div>
          </div>

          <motion.h1
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-3xl font-black text-gray-900 mb-4 tracking-tight"
          >
            {t('email_confirmed.title')}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-gray-500 text-lg mb-8 leading-relaxed"
          >
            {t('email_confirmed.subtitle')} <br className="hidden md:block" />
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="space-y-4"
          >
            {/* 4. Il pulsante ora chiama handleDeepLinkNavigation */}
            <button
              onClick={handleDeepLinkNavigation}
              className="w-full py-4 bg-gray-900 text-white rounded-xl font-bold hover:bg-black transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 flex items-center justify-center gap-2 group cursor-pointer"
            >
              <Smartphone size={20} />
              {t('email_confirmed.button')}
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </button>

            {count > 0 && <p className="text-sm text-gray-400 font-medium flex items-center justify-center gap-2">
              <Loader2 size={14} className="animate-spin" />
              {t('email_confirmed.redirect', { count })}
            </p>}

          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default MobileAuthConfirmed;